import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  getClients,
  getClient,
  getLettersForClient,
  getDisputesForClient,
  getDocuments,
  getDocumentUrl,
  getLetterPdfUrl,
} from "../api";

export default function ClientPortal() {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClientId = new URLSearchParams(location.search).get("client_id");

  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState(queryClientId || "");
  const [client, setClient] = useState(null);
  const [letters, setLetters] = useState([]);
  const [disputes, setDisputes] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingClient, setLoadingClient] = useState(false);
  const [error, setError] = useState("");

  const loadClients = useCallback(async () => {
    try {
      const data = await getClients();
      const list = Array.isArray(data) ? data : [];
      setClients(list);

      if (list.length === 0) {
        setSelectedClientId("");
        return;
      }

      setSelectedClientId((previous) => {
        const requested = queryClientId || previous;
        const exists = list.some(
          (item) => String(item.id) === String(requested)
        );
        return exists ? String(requested) : String(list[0].id);
      });
    } catch (err) {
      setError(err?.message || "Failed to load clients.");
      setClients([]);
    }
  }, [queryClientId]);

  const loadPortalData = useCallback(async (clientId) => {
    if (!clientId) {
      setClient(null);
      setLetters([]);
      setDisputes([]);
      setDocuments([]);
      setLoading(false);
      return;
    }

    try {
      setLoadingClient(true);
      setError("");

      const [clientData, letterData, disputeData, documentData] =
        await Promise.all([
          getClient(clientId),
          getLettersForClient(clientId),
          getDisputesForClient(clientId),
          getDocuments(),
        ]);

      setClient(clientData || null);
      setLetters(Array.isArray(letterData) ? letterData : []);
      setDisputes(Array.isArray(disputeData) ? disputeData : []);

      const allDocuments = Array.isArray(documentData) ? documentData : [];
      setDocuments(
        allDocuments.filter(
          (item) => String(item.client_id) === String(clientId)
        )
      );
    } catch (err) {
      setError(err?.message || "Failed to load client portal.");
      setClient(null);
      setLetters([]);
      setDisputes([]);
      setDocuments([]);
    } finally {
      setLoadingClient(false);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadClients();
  }, [loadClients]);

  useEffect(() => {
    loadPortalData(selectedClientId);
  }, [selectedClientId, loadPortalData]);

  const portalSummary = useMemo(() => {
    const summary = {
      totalLetters: letters.length,
      activeDisputes: 0,
      resolvedDisputes: 0,
      waiting: 0,
      readyForNextRound: 0,
    };

    disputes.forEach((dispute) => {
      const status = String(dispute.status || "open").toLowerCase();
      if (["resolved", "deleted", "completed"].includes(status)) {
        summary.resolvedDisputes += 1;
      } else {
        summary.activeDisputes += 1;
      }
    });

    const today = new Date();
    letters.forEach((letter) => {
      const status = String(letter.status || "sent").toLowerCase();
      const nextActionDate = parseDateValue(letter.next_action_date);

      if (
        status === "sent" &&
        nextActionDate &&
        !Number.isNaN(nextActionDate.getTime()) &&
        today >= nextActionDate
      ) {
        summary.readyForNextRound += 1;
      } else if (status === "sent") {
        summary.waiting += 1;
      }
    });

    return summary;
  }, [letters, disputes]);

  const latestLetters = useMemo(() => {
    return [...letters]
      .sort((a, b) => {
        const dateA = parseDateValue(a.created_at)?.getTime() || 0;
        const dateB = parseDateValue(b.created_at)?.getTime() || 0;
        return dateB - dateA;
      })
      .slice(0, 6);
  }, [letters]);

  const latestDisputes = useMemo(() => {
    return [...disputes]
      .sort((a, b) => {
        const dateA = parseDateValue(a.created_at)?.getTime() || 0;
        const dateB = parseDateValue(b.created_at)?.getTime() || 0;
        return dateB - dateA;
      })
      .slice(0, 6);
  }, [disputes]);

  const nextAction = useMemo(() => {
    if (portalSummary.readyForNextRound > 0) {
      return `${portalSummary.readyForNextRound} account${
        portalSummary.readyForNextRound === 1 ? "" : "s"
      } ready for the next dispute round.`;
    }

    if (portalSummary.waiting > 0) {
      return `${portalSummary.waiting} letter${
        portalSummary.waiting === 1 ? "" : "s"
      } waiting for bureau response.`;
    }

    if (letters.length === 0) {
      return "Your dispute specialist has not generated a letter yet.";
    }

    return "Your current dispute workflow is up to date.";
  }, [portalSummary, letters.length]);

  if (loading) {
    return (
      <div style={pageStyle}>
        <div style={loadingCardStyle}>Loading Client Portal...</div>
      </div>
    );
  }

  return (
    <div style={pageStyle}>
      <div style={headerRowStyle}>
        <div>
          <div style={eyebrowStyle}>Integrity Suite CRM</div>
          <h1 style={pageTitleStyle}>Client Portal</h1>
          <div style={subtitleStyle}>
            Progress, documents, letters, and dispute updates in one place.
          </div>
        </div>

        <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
          <button
            type="button"
            onClick={() => loadPortalData(selectedClientId)}
            style={primaryButtonStyle}
          >
            {loadingClient ? "Refreshing..." : "Refresh Portal"}
          </button>

          <button
            type="button"
            onClick={() => navigate(`/letters?client_id=${selectedClientId}`)}
            disabled={!selectedClientId}
            style={secondaryButtonStyle}
          >
            Open Internal Case
          </button>
        </div>
      </div>

      {error ? <div style={errorStyle}>{error}</div> : null}

      <div style={clientSelectorPanelStyle}>
        <div style={{ flex: 1, minWidth: "260px" }}>
          <div style={labelStyle}>Portal Preview Client</div>
          <select
            value={selectedClientId}
            onChange={(event) => {
              const value = event.target.value;
              setSelectedClientId(value);
              navigate(`/client-portal?client_id=${value}`, { replace: true });
            }}
            style={selectStyle}
          >
            {clients.length === 0 ? (
              <option value="">No clients available</option>
            ) : (
              clients.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.full_name || `Client ${item.id}`}
                </option>
              ))
            )}
          </select>
        </div>

        <div style={{ textAlign: "right" }}>
          <div style={labelStyle}>Portal Status</div>
          <div style={{ fontSize: "22px", fontWeight: "900", color: "#16a34a" }}>
            Active Preview
          </div>
        </div>
      </div>

      <div style={welcomePanelStyle}>
        <div>
          <div style={labelStyle}>Welcome</div>
          <div style={{ fontSize: "36px", fontWeight: "900", color: "#0f172a" }}>
            {client?.full_name || "Client"}
          </div>
          <div style={{ color: "#64748b", marginTop: "8px", fontSize: "16px" }}>
            {client?.email || "No email on file"}
          </div>
        </div>

        <div style={statusBadgeStyle}>Integrity AI Case Active</div>
      </div>

      <div style={summaryGridStyle}>
        <SummaryCard title="Active Disputes" value={portalSummary.activeDisputes} subtitle="Currently in progress" />
        <SummaryCard title="Resolved" value={portalSummary.resolvedDisputes} subtitle="Completed or deleted" />
        <SummaryCard title="Letters" value={portalSummary.totalLetters} subtitle="Generated for your case" />
        <SummaryCard title="Documents" value={documents.length} subtitle="Stored securely" />
      </div>

      <div style={twoColumnGridStyle}>
        <div style={panelStyle}>
          <div style={panelHeaderStyle}>Integrity AI Update</div>
          <div style={aiUpdateStyle}>{nextAction}</div>

          <div style={{ display: "grid", gap: "12px", marginTop: "18px" }}>
            <InfoRow label="Waiting for response" value={portalSummary.waiting} />
            <InfoRow label="Ready for next round" value={portalSummary.readyForNextRound} />
            <InfoRow label="Total dispute activity" value={letters.length + disputes.length} />
          </div>
        </div>

        <div style={panelStyle}>
          <div style={panelHeaderStyle}>Case Progress</div>
          <TimelineItem complete={documents.length > 0} title="Documents received" description={documents.length > 0 ? `${documents.length} document(s) on file` : "Waiting for document uploads"} />
          <TimelineItem complete={letters.length > 0} title="Dispute strategy prepared" description={letters.length > 0 ? `${letters.length} letter(s) generated` : "No letters generated yet"} />
          <TimelineItem complete={portalSummary.activeDisputes > 0} title="Disputes in progress" description={`${portalSummary.activeDisputes} active dispute(s)`} />
          <TimelineItem complete={portalSummary.resolvedDisputes > 0} title="Results recorded" description={`${portalSummary.resolvedDisputes} resolved dispute(s)`} />
        </div>
      </div>

      <div style={twoColumnGridStyle}>
        <div style={panelStyle}>
          <div style={panelHeaderStyle}>Recent Letters</div>
          {latestLetters.length === 0 ? (
            <div style={emptyStateStyle}>No letters are available yet.</div>
          ) : (
            <div style={{ display: "grid", gap: "12px" }}>
              {latestLetters.map((letter) => (
                <div key={letter.id} style={recordRowStyle}>
                  <div>
                    <div style={recordTitleStyle}>{letter.letter_type || letter.template || "Dispute Letter"}</div>
                    <div style={recordMetaStyle}>{letter.creditor || "Credit Bureau"} · Round {letter.round || 1}</div>
                    <div style={recordMetaStyle}>Created: {formatDate(letter.created_at)}</div>
                  </div>
                  <a href={getLetterPdfUrl(letter.id)} target="_blank" rel="noreferrer" style={linkButtonStyle}>View PDF</a>
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={panelStyle}>
          <div style={panelHeaderStyle}>Active Disputes</div>
          {latestDisputes.length === 0 ? (
            <div style={emptyStateStyle}>No disputes are available yet.</div>
          ) : (
            <div style={{ display: "grid", gap: "12px" }}>
              {latestDisputes.map((dispute) => (
                <div key={dispute.id} style={recordRowStyle}>
                  <div>
                    <div style={recordTitleStyle}>{dispute.creditor || dispute.account_name || "Dispute"}</div>
                    <div style={recordMetaStyle}>{dispute.bureau || "Unknown Bureau"} · {dispute.status || "Open"}</div>
                    <div style={recordMetaStyle}>{dispute.reason || dispute.dispute_reason || "Accuracy review"}</div>
                  </div>
                  <StatusPill value={dispute.status || "Open"} />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div style={{ ...panelStyle, marginBottom: "24px" }}>
        <div style={panelHeaderStyle}>Documents</div>
        {documents.length === 0 ? (
          <div style={emptyStateStyle}>No documents are available yet.</div>
        ) : (
          <div style={documentGridStyle}>
            {documents.map((document) => (
              <div key={document.id} style={documentCardStyle}>
                <div style={{ fontSize: "30px", marginBottom: "10px" }}>📄</div>
                <div style={recordTitleStyle}>{document.original_filename || document.filename || document.document_type || "Document"}</div>
                <div style={recordMetaStyle}>{document.document_type || "Client Document"}</div>
                <a href={getDocumentUrl(document.id)} target="_blank" rel="noreferrer" style={{ ...linkButtonStyle, marginTop: "14px" }}>Open</a>
              </div>
            ))}
          </div>
        )}
      </div>

      <div style={portalNoticeStyle}>
        <div style={{ fontSize: "22px", fontWeight: "900", color: "#0f172a" }}>Client Portal MVP</div>
        <div style={{ color: "#64748b", marginTop: "8px", lineHeight: 1.6 }}>
          This is the administrator preview. Separate client authentication, secure uploads, messaging, notifications, and billing will be added in the next portal phases.
        </div>
      </div>
    </div>
  );
}

function SummaryCard({ title, value, subtitle }) {
  return (
    <div style={summaryCardStyle}>
      <div style={labelStyle}>{title}</div>
      <div style={{ fontSize: "46px", fontWeight: "900", color: "#0f172a" }}>{value}</div>
      <div style={{ color: "#94a3b8", marginTop: "4px" }}>{subtitle}</div>
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div style={infoRowStyle}>
      <div style={{ color: "#64748b", fontWeight: "700" }}>{label}</div>
      <div style={{ color: "#0f172a", fontWeight: "900" }}>{value}</div>
    </div>
  );
}

function TimelineItem({ complete, title, description }) {
  return (
    <div style={timelineRowStyle}>
      <div style={{ ...timelineDotStyle, background: complete ? "#16a34a" : "#cbd5e1" }}>{complete ? "✓" : "•"}</div>
      <div>
        <div style={{ color: "#0f172a", fontWeight: "900" }}>{title}</div>
        <div style={{ color: "#64748b", marginTop: "4px" }}>{description}</div>
      </div>
    </div>
  );
}

function StatusPill({ value }) {
  const status = String(value || "Open").toLowerCase();
  const complete = ["resolved", "deleted", "completed"].includes(status);
  const pending = status === "pending";

  return (
    <div style={{
      padding: "8px 12px",
      borderRadius: "999px",
      background: complete ? "#dcfce7" : pending ? "#fef3c7" : "#dbeafe",
      color: complete ? "#166534" : pending ? "#92400e" : "#1d4ed8",
      fontWeight: "900",
      textTransform: "capitalize",
      whiteSpace: "nowrap",
    }}>
      {value}
    </div>
  );
}

function parseDateValue(value) {
  if (!value) return null;
  const text = String(value).trim();

  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) {
    const [year, month, day] = text.split("-").map(Number);
    return new Date(year, month - 1, day);
  }

  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}/.test(text)) {
    const [datePart, timePart] = text.split(" ");
    const [year, month, day] = datePart.split("-").map(Number);
    const [hour, minute, second] = timePart.split(":").map(Number);
    return new Date(year, month - 1, day, hour, minute, second || 0);
  }

  const date = new Date(text);
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatDate(value) {
  const date = parseDateValue(value);
  return date ? date.toLocaleString() : value || "Not available";
}

const pageStyle = { padding: "28px", minHeight: "100vh", background: "#f3f6fb" };
const headerRowStyle = { display: "flex", justifyContent: "space-between", alignItems: "center", gap: "20px", flexWrap: "wrap", marginBottom: "28px" };
const eyebrowStyle = { color: "#64748b", fontSize: "18px", fontWeight: "700", marginBottom: "8px" };
const pageTitleStyle = { margin: 0, color: "#0f172a", fontSize: "58px", lineHeight: 1, fontWeight: "900" };
const subtitleStyle = { color: "#64748b", marginTop: "12px", fontSize: "17px" };
const primaryButtonStyle = { padding: "15px 20px", borderRadius: "15px", border: "none", background: "#2563eb", color: "white", fontWeight: "900", cursor: "pointer" };
const secondaryButtonStyle = { ...primaryButtonStyle, background: "#0f172a" };
const clientSelectorPanelStyle = { display: "flex", justifyContent: "space-between", alignItems: "center", gap: "20px", flexWrap: "wrap", background: "white", border: "1px solid #e5e7eb", borderRadius: "22px", padding: "22px", marginBottom: "20px" };
const selectStyle = { width: "100%", padding: "14px 16px", borderRadius: "14px", border: "1px solid #cbd5e1", background: "white", fontSize: "16px" };
const welcomePanelStyle = { display: "flex", justifyContent: "space-between", alignItems: "center", gap: "20px", flexWrap: "wrap", padding: "26px", borderRadius: "24px", background: "linear-gradient(135deg, #eff6ff 0%, #ffffff 100%)", border: "1px solid #bfdbfe", marginBottom: "20px" };
const statusBadgeStyle = { padding: "12px 16px", borderRadius: "999px", background: "#dcfce7", color: "#166534", fontWeight: "900" };
const summaryGridStyle = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: "16px", marginBottom: "20px" };
const summaryCardStyle = { background: "white", border: "1px solid #e5e7eb", borderRadius: "20px", padding: "20px", boxShadow: "0 8px 20px rgba(15,23,42,0.05)" };
const twoColumnGridStyle = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))", gap: "20px", marginBottom: "24px" };
const panelStyle = { background: "white", border: "1px solid #e5e7eb", borderRadius: "24px", padding: "24px", boxShadow: "0 10px 24px rgba(15,23,42,0.06)" };
const panelHeaderStyle = { fontSize: "28px", color: "#0f172a", fontWeight: "900", marginBottom: "16px" };
const aiUpdateStyle = { padding: "18px", borderRadius: "16px", background: "#eff6ff", border: "1px solid #bfdbfe", color: "#1e3a8a", fontSize: "17px", fontWeight: "700", lineHeight: 1.6 };
const infoRowStyle = { display: "flex", justifyContent: "space-between", gap: "14px", padding: "14px 16px", borderRadius: "14px", background: "#f8fafc", border: "1px solid #e5e7eb" };
const timelineRowStyle = { display: "flex", alignItems: "flex-start", gap: "14px", padding: "14px 0", borderBottom: "1px solid #eef2f7" };
const timelineDotStyle = { width: "30px", height: "30px", borderRadius: "999px", color: "white", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: "900", flexShrink: 0 };
const recordRowStyle = { display: "flex", justifyContent: "space-between", alignItems: "center", gap: "16px", padding: "16px", borderRadius: "16px", background: "#f8fafc", border: "1px solid #e5e7eb" };
const recordTitleStyle = { color: "#0f172a", fontSize: "17px", fontWeight: "900" };
const recordMetaStyle = { color: "#64748b", marginTop: "5px", fontSize: "14px" };
const linkButtonStyle = { display: "inline-block", padding: "10px 14px", borderRadius: "12px", background: "#2563eb", color: "white", textDecoration: "none", fontWeight: "900", whiteSpace: "nowrap" };
const documentGridStyle = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))", gap: "14px" };
const documentCardStyle = { padding: "18px", borderRadius: "16px", background: "#f8fafc", border: "1px solid #e5e7eb" };
const portalNoticeStyle = { padding: "22px", borderRadius: "20px", background: "#fff7ed", border: "1px solid #fed7aa" };
const labelStyle = { color: "#64748b", fontSize: "14px", fontWeight: "800", marginBottom: "8px" };
const emptyStateStyle = { padding: "18px", color: "#94a3b8", background: "#f8fafc", borderRadius: "14px", border: "1px dashed #cbd5e1" };
const errorStyle = { padding: "16px 18px", marginBottom: "20px", borderRadius: "16px", border: "2px solid #ef4444", background: "white", color: "#991b1b" };
const loadingCardStyle = { background: "white", padding: "26px", borderRadius: "20px", fontSize: "20px", fontWeight: "900", color: "#0f172a" };
